import React, { useState, useCallback } from 'react';
import {
  Modal,
  Tabs,
  Card,
  Text,
  TextInput,
  Textarea,
  Button,
  Group,
  Stack,
  Badge,
  Table,
  ActionIcon,
  Checkbox,
  Alert,
  Loader,
  Grid,
  Select,
  NumberInput,
  Code,
} from '@mantine/core';
import {
  IconTrash,
  IconRefresh,
  IconFlame,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconSearch,
  IconSettings,
} from '@tabler/icons-react';
import { useCacheMetrics } from '../hooks/useCacheMetrics';

interface CacheInvalidationManagerProps {
  onClose: () => void;
  onInvalidate: () => void;
}

export const CacheInvalidationManager: React.FC<CacheInvalidationManagerProps> = ({
  onClose,
  onInvalidate,
}) => {
  const {
    invalidatePattern,
    invalidateKey,
    flushNamespace,
    warmCache,
    analyzePatterns,
  } = useCacheMetrics();

  // Pattern Invalidation State
  const [patternInput, setPatternInput] = useState('');
  const [patternReason, setPatternReason] = useState('');
  const [patternInvalidating, setPatternInvalidating] = useState(false);

  // Key Invalidation State
  const [keyInput, setKeyInput] = useState('');
  const [keyReason, setKeyReason] = useState('');
  const [keyInvalidating, setKeyInvalidating] = useState(false);

  // Namespace Flush State
  const [namespaceInput, setNamespaceInput] = useState('');
  const [namespaceReason, setNamespaceReason] = useState('');
  const [namespaceConfirm, setNamespaceConfirm] = useState('');
  const [namespaceFlushLoading, setNamespaceFlushLoading] = useState(false);

  // Cache Warming State
  const [warmPattern, setWarmPattern] = useState('');
  const [warmStrategy, setWarmStrategy] = useState<'eager' | 'lazy'>('eager');
  const [warmPriority, setWarmPriority] = useState<number>(5);
  const [warmBatchSize, setWarmBatchSize] = useState<number>(100);
  const [warmingCache, setWarmingCache] = useState(false);

  // Bulk Operations State
  const [bulkPatterns, setBulkPatterns] = useState<string[]>(['']);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkInvalidating, setBulkInvalidating] = useState(false);

  // Pattern Analysis State
  const [analyzingPatterns, setAnalyzingPatterns] = useState(false);

  // ============================================================================
  // Pattern Invalidation Handlers
  // ============================================================================

  const handleInvalidatePattern = useCallback(async () => {
    if (!patternInput.trim()) {
      return;
    }

    setPatternInvalidating(true);
    try {
      const success = await invalidatePattern(
        patternInput.trim(),
        patternReason.trim() || 'Manual invalidation'
      );
      if (success) {
        setPatternInput('');
        setPatternReason('');
        onInvalidate();
      }
    } finally {
      setPatternInvalidating(false);
    }
  }, [patternInput, patternReason, invalidatePattern, onInvalidate]);

  // ============================================================================
  // Key Invalidation Handlers
  // ============================================================================

  const handleInvalidateKey = useCallback(async () => {
    if (!keyInput.trim()) {
      return;
    }

    setKeyInvalidating(true);
    try {
      const success = await invalidateKey(keyInput.trim());
      if (success) {
        setKeyInput('');
        setKeyReason('');
        onInvalidate();
      }
    } finally {
      setKeyInvalidating(false);
    }
  }, [keyInput, keyReason, invalidateKey, onInvalidate]);

  // ============================================================================
  // Namespace Flush Handlers
  // ============================================================================

  const handleFlushNamespace = useCallback(async () => {
    if (!namespaceInput.trim()) {
      return;
    }

    // Require confirmation (user must type "FLUSH")
    if (namespaceConfirm !== 'FLUSH') {
      return;
    }

    setNamespaceFlushLoading(true);
    try {
      const success = await flushNamespace(namespaceInput.trim());
      if (success) {
        setNamespaceInput('');
        setNamespaceReason('');
        setNamespaceConfirm('');
        onInvalidate();
      }
    } finally {
      setNamespaceFlushLoading(false);
    }
  }, [namespaceInput, namespaceReason, namespaceConfirm, flushNamespace, onInvalidate]);

  // ============================================================================
  // Cache Warming Handlers
  // ============================================================================

  const handleWarmCache = useCallback(async () => {
    if (!warmPattern.trim()) {
      return;
    }

    setWarmingCache(true);
    try {
      const success = await warmCache([warmPattern.trim()]);
      if (success) {
        setWarmPattern('');
        onInvalidate();
      }
    } finally {
      setWarmingCache(false);
    }
  }, [warmPattern, warmStrategy, warmPriority, warmBatchSize, warmCache, onInvalidate]);

  // ============================================================================
  // Bulk Operations Handlers
  // ============================================================================

  const handleAddBulkPattern = useCallback(() => {
    setBulkPatterns([...bulkPatterns, '']);
  }, [bulkPatterns]);

  const handleRemoveBulkPattern = useCallback((index: number) => {
    setBulkPatterns(bulkPatterns.filter((_: any, i) => i !== index));
  }, [bulkPatterns]);

  const handleUpdateBulkPattern = useCallback((index: number, value: string) => {
    const updated = [...bulkPatterns];
    updated[index] = value;
    setBulkPatterns(updated);
  }, [bulkPatterns]);

  const handleBulkInvalidate = useCallback(async () => {
    const validPatterns = bulkPatterns.filter(p => p.trim().length > 0);
    if (validPatterns.length === 0) {
      return;
    }

    setBulkInvalidating(true);
    try {
      const results = await Promise.all(
        validPatterns.map(pattern =>
          invalidatePattern(pattern, bulkReason.trim() || 'Bulk invalidation')
        )
      );
      const successCount = results.filter(r => r).length;
      if (successCount > 0) {
        setBulkPatterns(['']);
        setBulkReason('');
        onInvalidate();
      }
    } finally {
      setBulkInvalidating(false);
    }
  }, [bulkPatterns, bulkReason, invalidatePattern, onInvalidate]);

  // ============================================================================
  // Pattern Analysis Handlers
  // ============================================================================

  const handleAnalyzePatterns = useCallback(async () => {
    setAnalyzingPatterns(true);
    try {
      await analyzePatterns();
      onInvalidate();
    } finally {
      setAnalyzingPatterns(false);
    }
  }, [analyzePatterns, onInvalidate]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconSettings size={24} />
          <Text size="xl" fw={700}>Cache Invalidation Manager</Text>
        </Group>
      }
      size="xl"
      overlayProps={{ opacity: 0.55, blur: 3 }}
    >
      <Tabs defaultValue="pattern">
        {/* ====================================================================== */}
        {/* Pattern Invalidation Tab */}
        {/* ====================================================================== */}
        <Tabs.List>
          <Tabs.Tab value="pattern" leftSection={<IconSearch size={16} />}>
            Pattern Invalidation
          </Tabs.Tab>
          <Tabs.Tab value="key" leftSection={<IconTrash size={16} />}>
            Key Invalidation
          </Tabs.Tab>
          <Tabs.Tab value="namespace" leftSection={<IconFlame size={16} />}>
            Namespace Flush
          </Tabs.Tab>
          <Tabs.Tab value="warming" leftSection={<IconRefresh size={16} />}>
            Cache Warming
          </Tabs.Tab>
          <Tabs.Tab value="bulk" leftSection={<IconSettings size={16} />}>
            Bulk Operations
          </Tabs.Tab>
        </Tabs.List>

        {/* ====================================================================== */}
        {/* Pattern Invalidation Panel */}
        {/* ====================================================================== */}
        <Tabs.Panel value="pattern" pt="md">
          <Card shadow="sm" padding="lg">
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Invalidate cache keys matching a pattern (supports wildcards: * and ?)
              </Text>

              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                <Text size="sm">
                  Examples: <Code>user:*</Code>, <Code>tenant:abc123:*</Code>, <Code>workflow:*:status</Code>
                </Text>
              </Alert>

              <TextInput
                label="Cache Pattern"
                placeholder="e.g., user:*, tenant:abc123:*"
                value={patternInput}
                onChange={(e) => setPatternInput(e.currentTarget.value)}
                required
                leftSection={<IconSearch size={16} />}
              />

              <Textarea
                label="Reason (Optional)"
                placeholder="Why are you invalidating this pattern?"
                value={patternReason}
                onChange={(e) => setPatternReason(e.currentTarget.value)}
                minRows={2}
              />

              <Group justify="flex-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPatternInput('');
                    setPatternReason('');
                  }}
                >
                  Clear
                </Button>
                <Button
                  color="red"
                  onClick={handleInvalidatePattern}
                  disabled={!patternInput.trim() || patternInvalidating}
                  loading={patternInvalidating}
                  leftSection={<IconTrash size={16} />}
                >
                  Invalidate Pattern
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        {/* ====================================================================== */}
        {/* Key Invalidation Panel */}
        {/* ====================================================================== */}
        <Tabs.Panel value="key" pt="md">
          <Card shadow="sm" padding="lg">
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Invalidate a single cache key by exact match
              </Text>

              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                <Text size="sm">
                  Example: <Code>user:12345</Code>, <Code>tenant:abc123:workflow:def456</Code>
                </Text>
              </Alert>

              <TextInput
                label="Cache Key"
                placeholder="e.g., user:12345"
                value={keyInput}
                onChange={(e) => setKeyInput(e.currentTarget.value)}
                required
                leftSection={<IconSearch size={16} />}
              />

              <Textarea
                label="Reason (Optional)"
                placeholder="Why are you invalidating this key?"
                value={keyReason}
                onChange={(e) => setKeyReason(e.currentTarget.value)}
                minRows={2}
              />

              <Group justify="flex-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setKeyInput('');
                    setKeyReason('');
                  }}
                >
                  Clear
                </Button>
                <Button
                  color="red"
                  onClick={handleInvalidateKey}
                  disabled={!keyInput.trim() || keyInvalidating}
                  loading={keyInvalidating}
                  leftSection={<IconTrash size={16} />}
                >
                  Invalidate Key
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        {/* ====================================================================== */}
        {/* Namespace Flush Panel */}
        {/* ====================================================================== */}
        <Tabs.Panel value="namespace" pt="md">
          <Card shadow="sm" padding="lg">
            <Stack gap="md">
              <Alert icon={<IconAlertCircle size={16} />} color="red" title="Danger Zone">
                <Text size="sm">
                  Flushing a namespace will delete ALL cache keys in that namespace. This operation cannot be undone.
                </Text>
              </Alert>

              <Text size="sm" c="dimmed">
                Flush all cache keys in a specific namespace
              </Text>

              <TextInput
                label="Namespace"
                placeholder="e.g., user, workflow, tenant"
                value={namespaceInput}
                onChange={(e) => setNamespaceInput(e.currentTarget.value)}
                required
                leftSection={<IconFlame size={16} />}
              />

              <Textarea
                label="Reason (Optional)"
                placeholder="Why are you flushing this namespace?"
                value={namespaceReason}
                onChange={(e) => setNamespaceReason(e.currentTarget.value)}
                minRows={2}
              />

              <TextInput
                label="Confirmation (Type 'FLUSH' to confirm)"
                placeholder="FLUSH"
                value={namespaceConfirm}
                onChange={(e) => setNamespaceConfirm(e.currentTarget.value)}
                required
                error={namespaceConfirm.length > 0 && namespaceConfirm !== 'FLUSH' ? 'Must type FLUSH' : undefined}
              />

              <Group justify="flex-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setNamespaceInput('');
                    setNamespaceReason('');
                    setNamespaceConfirm('');
                  }}
                >
                  Clear
                </Button>
                <Button
                  color="red"
                  onClick={handleFlushNamespace}
                  disabled={!namespaceInput.trim() || namespaceConfirm !== 'FLUSH' || namespaceFlushLoading}
                  loading={namespaceFlushLoading}
                  leftSection={<IconFlame size={16} />}
                >
                  Flush Namespace
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        {/* ====================================================================== */}
        {/* Cache Warming Panel */}
        {/* ====================================================================== */}
        <Tabs.Panel value="warming" pt="md">
          <Card shadow="sm" padding="lg">
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Pre-load cache keys to improve performance
              </Text>

              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                <Text size="sm">
                  Warming strategy: <strong>Eager</strong> loads immediately, <strong>Lazy</strong> loads on first access
                </Text>
              </Alert>

              <TextInput
                label="Cache Pattern"
                placeholder="e.g., user:*, workflow:*"
                value={warmPattern}
                onChange={(e) => setWarmPattern(e.currentTarget.value)}
                required
                leftSection={<IconRefresh size={16} />}
              />

              <Select
                label="Warming Strategy"
                value={warmStrategy}
                onChange={(value) => setWarmStrategy(value as 'eager' | 'lazy')}
                data={[
                  { value: 'eager', label: 'Eager (Load immediately)' },
                  { value: 'lazy', label: 'Lazy (Load on first access)' },
                ]}
              />

              <NumberInput
                label="Priority (1-10)"
                description="Higher priority keys are warmed first"
                value={warmPriority}
                onChange={(value) => setWarmPriority(Number(value) || 5)}
                min={1}
                max={10}
              />

              <NumberInput
                label="Batch Size"
                description="Number of keys to warm per batch"
                value={warmBatchSize}
                onChange={(value) => setWarmBatchSize(Number(value) || 100)}
                min={10}
                max={1000}
                step={10}
              />

              <Group justify="flex-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setWarmPattern('');
                    setWarmStrategy('eager');
                    setWarmPriority(5);
                    setWarmBatchSize(100);
                  }}
                >
                  Clear
                </Button>
                <Button
                  color="blue"
                  onClick={handleWarmCache}
                  disabled={!warmPattern.trim() || warmingCache}
                  loading={warmingCache}
                  leftSection={<IconRefresh size={16} />}
                >
                  Warm Cache
                </Button>
              </Group>
            </Stack>
          </Card>
        </Tabs.Panel>

        {/* ====================================================================== */}
        {/* Bulk Operations Panel */}
        {/* ====================================================================== */}
        <Tabs.Panel value="bulk" pt="md">
          <Card shadow="sm" padding="lg">
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Invalidate multiple cache patterns in one operation
              </Text>

              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                <Text size="sm">
                  Add multiple patterns to invalidate them all at once
                </Text>
              </Alert>

              {bulkPatterns.map((pattern, index) => (
                <Group key={index} ta="right" gap="xs">
                  <TextInput
                    label={index === 0 ? 'Cache Patterns' : undefined}
                    placeholder="e.g., user:*, workflow:*"
                    value={pattern}
                    onChange={(e) => handleUpdateBulkPattern(index, e.currentTarget.value)}
                    style={{ flex: 1 }}
                    leftSection={<IconSearch size={16} />}
                  />
                  {bulkPatterns.length > 1 && (
                    <ActionIcon
                      color="red"
                      variant="light"
                      onClick={() => handleRemoveBulkPattern(index)}
                      mb={index === 0 ? 0 : undefined}
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  )}
                </Group>
              ))}

              <Button
                variant="light"
                onClick={handleAddBulkPattern}
                fullWidth
              >
                + Add Pattern
              </Button>

              <Textarea
                label="Reason (Optional)"
                placeholder="Why are you invalidating these patterns?"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.currentTarget.value)}
                minRows={2}
              />

              <Group justify="flex-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setBulkPatterns(['']);
                    setBulkReason('');
                  }}
                >
                  Clear All
                </Button>
                <Button
                  color="red"
                  onClick={handleBulkInvalidate}
                  disabled={bulkPatterns.filter(p => p.trim()).length === 0 || bulkInvalidating}
                  loading={bulkInvalidating}
                  leftSection={<IconTrash size={16} />}
                >
                  Invalidate All ({bulkPatterns.filter(p => p.trim()).length})
                </Button>
              </Group>
            </Stack>
          </Card>

          {/* Pattern Analysis Card */}
          <Card shadow="sm" padding="lg" mt="md">
            <Stack gap="md">
              <Text fw={600}>Cache Pattern Analysis</Text>
              <Text size="sm" c="dimmed">
                Analyze current cache usage patterns and get optimization recommendations
              </Text>

              <Button
                color="blue"
                onClick={handleAnalyzePatterns}
                loading={analyzingPatterns}
                leftSection={<IconSearch size={16} />}
              >
                Analyze Cache Patterns
              </Button>
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
};
